/**
 * Hook to track unread community messages.
 * Uses localStorage to persist the last-seen timestamp per room.
 * Polls the community messages endpoint to detect new messages.
 */
import { useMemo, useCallback } from "react";
import { trpc } from "@/lib/trpc";

const STORAGE_KEY = "ayu-community-last-seen";

function getLastSeen(): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setLastSeenForRoom(roomSlug: string, timestamp: number) {
  try {
    const current = getLastSeen();
    current[roomSlug] = timestamp;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // localStorage not available
  }
}

/**
 * Returns the total number of unread messages across all rooms,
 * and a function to mark a room as read.
 */
export function useCommunityUnread() {
  // Fetch rooms
  const { data: rooms } = trpc.community.getRooms.useQuery(undefined, {
    refetchInterval: 30000, // check every 30s
    staleTime: 15000,
  });

  // Fetch messages for the default "allgemein" room (most active)
  const { data: allgemeinData } = trpc.community.getMessages.useQuery(
    { roomSlug: "allgemein", limit: 50 },
    { refetchInterval: 15000, staleTime: 10000 }
  );

  const unreadCount = useMemo(() => {
    if (!allgemeinData?.messages?.length) return 0;

    const lastSeen = getLastSeen();
    const lastSeenTime = lastSeen["allgemein"] ?? 0;

    // Count messages newer than last seen
    return allgemeinData.messages.filter((msg) => {
      const msgTime = new Date(msg.createdAt).getTime();
      return msgTime > lastSeenTime;
    }).length;
  }, [allgemeinData?.messages]);

  const markRoomAsRead = useCallback((roomSlug: string) => {
    setLastSeenForRoom(roomSlug, Date.now());
  }, []);

  const markAllAsRead = useCallback(() => {
    const now = Date.now();
    if (rooms) {
      for (const room of rooms) {
        setLastSeenForRoom(room.slug, now);
      }
    }
    // Always mark allgemein
    setLastSeenForRoom("allgemein", now);
  }, [rooms]);

  return { unreadCount, markRoomAsRead, markAllAsRead };
}
